import { type Dispatch, type SetStateAction } from "react";
import type { printOnDemand } from "common/utils/models";
import type { Side } from "@design-lab/shared/types";
import { mapPdpQuantitiesToDesignLab } from "@design-lab/shared/lib/pdp-persistence";
import type { UrlFlagsContext } from "@design-lab/widgets/design-lab-upload-artwork/model/url-flags.machine";
import { processMultiFileUpload, processSingleFileUpload } from "./workflowHelpers";

export interface WorkflowHandlerParams {
  urlFlags: UrlFlagsContext | null;
  productQueries: Array<{ data: printOnDemand.getProduct.Response | undefined }>;
  fetchProduct: (catalogNumber: string) => Promise<any>;
  selectProductComposition: (options: any) => Promise<void>;
  uploadArtwork: (params: any) => Promise<void>;
  artworkForm: any;
  isInitialState: () => boolean;
  setAutoUploadFile: Dispatch<SetStateAction<File | null>>;
  setExistingArtworkId: Dispatch<SetStateAction<string | undefined>>;
  setIsDesignLibraryOpen: Dispatch<SetStateAction<boolean>>;
  setIsCanvaOpen: Dispatch<SetStateAction<boolean>>;
  setIsAiDesignOpen: Dispatch<SetStateAction<boolean>>;
  originalArtworkRef: React.MutableRefObject<any>;
}

// Process catalog selection with merge strategy
export async function processCatalogSelection(params: WorkflowHandlerParams) {
  const { urlFlags, productQueries, fetchProduct, selectProductComposition, artworkForm } = params;

  if (!urlFlags?.transientData.catalogNumber || !urlFlags?.transientData.activeComposition) {
    return undefined;
  }

  const { catalogNumber, activeComposition, pdpQuantities, pdpSizeData } = urlFlags.transientData;

  // Find the product for this catalog
  let productQuery = productQueries.find(query => query.data?.catalogNumber === catalogNumber);

  if (!productQuery?.data) {
    // Product not loaded yet, try to fetch it
    const product = await fetchProduct(catalogNumber);
    if (!product) {
      throw new Error(`Product ${catalogNumber} not found`);
    }
    productQuery = { data: product };
  }

  const targetComposition = productQuery?.data?.compositions?.find(
    (comp: printOnDemand.getProduct.Composition) => comp.colorName === activeComposition
  );

  if (!targetComposition) {
    throw new Error(`Composition ${activeComposition} not found for product ${catalogNumber}`);
  }

  // Build variants from PDP data if available
  let variants: Record<string, number> = {};
  if (pdpQuantities && pdpSizeData) {
    try {
      const pdpQty = JSON.parse(pdpQuantities);
      const pdpSize = JSON.parse(pdpSizeData);
      variants = mapPdpQuantitiesToDesignLab(pdpQty, pdpSize, targetComposition.variants);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to parse PDP data:", error);
      window.Honeybadger?.notify("Failed to parse PDP data", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          error_object: error instanceof Error ? error.toString() : String(error),
          stack_trace: error instanceof Error ? error.stack : null,
          function: "processCatalogSelection",
          catalogNumber,
          activeComposition
        }
      });
    }
  }

  // Apply the composition selection (merge strategy)
  await selectProductComposition({
    catalogNumber,
    colorName: targetComposition.colorName,
    variants
    // Note: replaceIfInitial would need to be added to selectProductComposition
  });

  // Set the placement if specified
  if (urlFlags.transientData.side) {
    const targetPlacement = urlFlags.transientData.side === "back" ? "back" : "front";
    artworkForm.setValue("currentPlacement", targetPlacement);
  }

  return targetComposition;
}

// Process auto-upload workflow
export async function processAutoUpload(params: WorkflowHandlerParams) {
  const { urlFlags, uploadArtwork, setAutoUploadFile, setExistingArtworkId } = params;

  // Check for both legacy artworkUrls and new uploadUrls
  const hasLegacyUpload =
    urlFlags?.transientData.artworkUrls && urlFlags?.transientData.fileContentType;
  const hasNewUpload =
    urlFlags?.transientData.uploadUrls && urlFlags?.transientData.fileContentType;

  if (!hasLegacyUpload && !hasNewUpload) {
    throw new Error("Missing auto-upload parameters");
  }

  // First apply catalog selection
  const targetComposition = await processCatalogSelection(params);

  try {
    // Handle new upload_urls format (multiple files)
    if (hasNewUpload) {
      const { files, errors } = await processMultiFileUpload(
        urlFlags.transientData.uploadUrls!,
        urlFlags.transientData.fileContentType!
      );

      if (files.length === 0) {
        const errorMessages = errors.map(e => `${e.sideKey}: ${e.error}`).join(", ");
        throw new Error(`No valid files found: ${errorMessages}`);
      }

      // Upload all valid files
      const uploads: Promise<void>[] = [];

      for (const { sideKey, file } of files) {
        const uploadParams: any = {
          file,
          side: sideKey,
          ...(urlFlags.transientData.source && { source: urlFlags.transientData.source }),
          ...(targetComposition && { sourceComposition: targetComposition })
        };

        uploads.push(uploadArtwork(uploadParams));

        // Set the first file as the auto-upload file for backward compatibility
        if (uploads.length === 1) {
          setAutoUploadFile(file);
        }
      }

      await Promise.all(uploads);
    }
    // Handle legacy artwork_urls format (single file)
    else if (hasLegacyUpload) {
      const result = await processSingleFileUpload(
        urlFlags.transientData.artworkUrls!,
        urlFlags.transientData.fileContentType!
      );

      if (result.error || !result.file) {
        throw new Error(result.error || "Failed to process file");
      }

      setAutoUploadFile(result.file);
      if (result.artworkId) {
        setExistingArtworkId(result.artworkId);
      }

      // Upload the artwork
      const uploadSide = (urlFlags.transientData.side || "front") as Side;
      const uploadParams: any = {
        file: result.file,
        side: uploadSide,
        ...(urlFlags.transientData.source && { source: urlFlags.transientData.source }),
        ...(result.artworkId && { existingArtworkId: result.artworkId }),
        ...(targetComposition && { sourceComposition: targetComposition })
      };

      await uploadArtwork(uploadParams);
    }
  } catch (error) {
    throw new Error(
      `Auto-upload failed: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

// Process design library workflow
export async function processDesignLibrary(params: WorkflowHandlerParams) {
  const { artworkForm, setIsDesignLibraryOpen, originalArtworkRef } = params;

  // Apply catalog selection first
  await processCatalogSelection(params);

  // Store current artwork for potential restoration
  originalArtworkRef.current = artworkForm.getValues();

  // Open the modal
  setIsDesignLibraryOpen(true);
}

// Process Canva workflow
export async function processCanva(params: WorkflowHandlerParams) {
  const { artworkForm, setIsCanvaOpen, originalArtworkRef } = params;

  // Apply catalog selection first
  await processCatalogSelection(params);

  // Store current artwork for potential restoration
  originalArtworkRef.current = artworkForm.getValues();

  // Open the modal
  setIsCanvaOpen(true);
}

// Process AI design workflow
export async function processAiDesign(params: WorkflowHandlerParams) {
  const { setIsAiDesignOpen } = params;

  // Apply catalog selection first
  await processCatalogSelection(params);

  // Open the modal/interface
  setIsAiDesignOpen(true);
}

// Process PDP only workflow (no modal, just apply catalog/composition)
export async function processPdpOnly(params: WorkflowHandlerParams) {
  await processCatalogSelection(params);
}
